import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import backend from '@api/backend'
import {
  Ticket,
  TicketCategory,
  TicketStatus,
  TicketSummary,
  TicketUpdate,
} from '@data/tickets/model/ticket'
import { DeviceIDTypes } from '@data/device/model/device'

export interface TicketsQueryProps {
  startDate: string,
  endDate: string,
  status: TicketStatus[],
  issueType: TicketCategory[]
}

export const useTickets = (props: TicketsQueryProps | typeof skipToken) => {
  return useQuery<Ticket[]>({
    queryKey: ['deviceTicket'],
    queryFn: props === skipToken
      ? skipToken
      : ({ signal }) => getAll(signal, props),
    enabled: true,
    staleTime: 1000 * 60 * 2, // 60 minutes,
  })
}

export const useTicketByDeviceId = (deviceId: string) => {
  return useQuery<Ticket[]>({
    queryKey: ['deviceTicket', deviceId],
    queryFn: ({ signal }) => getByDeviceId(deviceId, signal),
    enabled: true,
    staleTime: 1000 * 60 * 2,
  })
}

export const useTicketById = (deviceId: string, ticketId: number, enabled = true) => {
  return useQuery<Ticket>({
    queryKey: ['deviceTicket', deviceId, ticketId],
    queryFn: ({ signal }) => getById(deviceId, ticketId, signal),
    enabled,
    staleTime: 1000 * 60 * 2,
  })
}

export const useGetTicketQuery = (name: any) => {
  const queryClient = useQueryClient()
  return queryClient.getQueryData(name)
}

export const useTicketsSummary = (props: TicketsQueryProps | typeof skipToken) => {
  return useQuery<TicketSummary[]>({
    queryKey: ['deviceTicketSummary'],
    queryFn: props === skipToken
      ? skipToken
      : ({ signal }) => getTicketSummary(signal, props),
    enabled: true,
    staleTime: 1000 * 60 * 2, // 60 minutes,
  })
}

const getAll = async (signal: AbortSignal, {
  startDate,
  endDate,
  status,
  issueType,
}: TicketsQueryProps): Promise<Ticket[]> => {
  const response = await backend.get(`/tickets/`, {
    signal,
    params: {
      startDate,
      endDate,
      status,
      issueType,
    },
    paramsSerializer: {
      indexes: null,
    },
  })
  return response.data
}

const getByDeviceId = async (deviceId: string, signal: AbortSignal): Promise<Ticket[]> => {
  const response = await backend.get(`/tickets/${deviceId}`, {
    signal,
  })
  return response.data
}

const getById = async (deviceId: string, ticketId: number, signal: AbortSignal): Promise<Ticket> => {
  const response = await backend.get(`/tickets/${deviceId}/${ticketId}`, {
    signal,
    params: { idType: DeviceIDTypes.dUuid }
  })
  return response.data
}

export const useTicketUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateTicket,
    onSuccess: (data: Ticket, { dUuid, ticketId }) => {
      queryClient.setQueryData(['deviceTicket'], (prev: Ticket[] = []) => {
        if (data.status === 'closed') {
          return prev.filter(ticket => ticket.id !== data.id)
        }
        return prev.map(ticket => ticket.id === data.id ? data : ticket)
      })

      queryClient.setQueryData(['deviceTicket', dUuid, ticketId], (_prev: Ticket | undefined) => {
        if (data.status === 'closed') return undefined
        return data
      })

      queryClient.setQueryData(['deviceTicketSummary'], (prev: TicketSummary[] = []) => {
        if (data.status === 'closed') {
          return prev.filter(summary => summary.id !== data.id)
        }
        return prev.map(summary =>
          summary.id === data.id ? { ...summary, status: data.status } : summary
        )
      })

      // A list reload already in flight was built by the server before this change, and would
      // overwrite the edits above when it lands: a ticket closed during a slow reload came
      // back as pending_review. Restart it instead. invalidateQueries cancels the in-flight
      // request and fetches again, and the server now has the change. Nothing extra is sent
      // when no reload is running.
      for (const queryKey of [['deviceTicket'], ['deviceTicketSummary']]) {
        if (queryClient.isFetching({ queryKey, exact: true }) > 0) {
          void queryClient.invalidateQueries({ queryKey, exact: true })
        }
      }
    },
//     onError: () => {
//       displayToast({
//         type: 'error',
//         message: 'Something went wrong',
//       })
//     },
  })
}

const updateTicket = async (data: TicketUpdate): Promise<Ticket> => {
  const response = await backend.patch(`/tickets/${data.dUuid}/${data.ticketId}`,
    data,
    { params: { idType: DeviceIDTypes.dUuid } },
  )
  return response.data
}

const getTicketSummary = async (signal: AbortSignal, {
  startDate,
  endDate,
  status,
  issueType,
}: TicketsQueryProps): Promise<TicketSummary[]> => {
  const response = await backend.get('/tickets/summary', {
    signal,
    params: {
      startDate,
      endDate,
      status,
      issueType,
    },
    paramsSerializer: {
      indexes: null,
    },
  })
  return response.data
}